import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GeoHelper } from '@classes/geo-helper';
import { GEO_LOCATION_GRANTED, LocalStorage } from '@classes/local-storage';
import { ObjectHelper } from '@classes/object-helper';
import { UtilityHelper } from '@classes/utility-helper';
import { environment } from '@environments/environment';
import { Address } from '@models/address.model';
import { GeoData } from '@models/geo-data.model';
import { State } from '@models/state.model';
import { catchError, combineLatest, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class GeoService {
    constructor(
        private http: HttpClient
    ) {}

    getGeoData(address: Address): Observable<GeoData> {
        const record = ObjectHelper.convertObjectToStringRecord(address);
        const queryString = UtilityHelper.getQueryString(record);
        const url = `${environment.apiBaseUrl}/geo/geo-data?${queryString}`;
        return this.http.get<GeoData>(url);
    }

    getAddress(latitude: number, longitude: number): Observable<Address> {
        const record = ObjectHelper.convertObjectToStringRecord({ latitude, longitude });
        const queryString = UtilityHelper.getQueryString(record);
        const url = `${environment.apiBaseUrl}/geo/address?${queryString}`;
        return this.http.get<Address>(url);
    }

    getState(latitude: number, longitude: number): Observable<State> {
        const record = ObjectHelper.convertObjectToStringRecord({ latitude, longitude });
        const queryString = UtilityHelper.getQueryString(record);
        const url = `${environment.apiBaseUrl}/geo/state?${queryString}`;
        return this.http.get<State>(url);
    }

    tryGetGeoData(address?: Address, getCurrentPositionFn?: Function, getAddressGeoDataFn?: Function): Observable<GeoData> {
        const granted$ = combineLatest([
            GeoHelper.getLocationPromptStatus(),
            LocalStorage.get<boolean>(GEO_LOCATION_GRANTED, 'boolean')
        ]).pipe(
            map(([status, granted]) => status === 'granted' || (granted && UtilityHelper.isIOS()))
        );
        const addressGeoData$ = address ? this.getAddressGeoData(address, getAddressGeoDataFn) : of();
        return granted$.pipe(
            switchMap(granted => {
                if (!granted) {
                    return addressGeoData$;
                }

                return this.getCurrentPosition(address, getCurrentPositionFn).pipe(
                    catchError(error => {
                        console.error(error);
                        return addressGeoData$;
                    }),
                    tap(x => console.log('geoData', x)),
                    shareReplay()
                );
            })
        );
    }

    private getCurrentPosition(address?: Address, fn?: Function): Observable<GeoData> {
        return GeoHelper.getCurrentPositionGeoData().pipe(
            tap(x => console.log('getCurrentPositionGeoData', x)),
            tap(geoData => fn?.(geoData)),
            catchError(error => {
                console.error(error);
                return address ? this.getAddressGeoData(address, fn) : [];
            }),
        );
    }

    private getAddressGeoData(address: Address, fn?: Function): Observable<GeoData> {
        return this.getGeoData(address).pipe(
            tap(geoData => fn?.(geoData)),
            tap(x => console.log('addressGeoData', x)),
            shareReplay()
        );
    }

    private getGeoDataFromResponse(response: any): GeoData {
        const result = response.results[0];
        if (!result) {
            throw new Error(`Geocode: ${response.error_message}`);
        }

        const coords = result.geometry.location;
        const latitude = coords.lat;
        const longitude = coords.lng;
        const geohash = GeoHelper.getGeohash(latitude, longitude);
        const geoData: GeoData = {
            latitude,
            longitude,
            geohash
        };
        return geoData;
    }

    private getReverseGeocodingData(response: any, type: string): string {
        const obj = response.results[0].address_components.find((x: { types: string; }) => x.types.includes(type));
        return obj?.short_name;
    }
}
